// Editable text field with the change shown as an inline word diff: deleted
// words struck through, added words in green, inside the same control — no
// separate before→after line. Click (or Enter/Space) to switch to a plain
// editor seeded with the current text; blur re-renders the diff of the
// original value vs. whatever the reviewer now has.
import React, { useMemo, useRef, useState } from 'react';
import { useAutosizeTextArea } from 'components/DesignSystems/New/PratusTextArea';
import { wordDiff } from '../wordDiff';

const SEGMENT_CLASSES: Record<string, string> = {
  del: 'tw:text-[color:var(--text-weak)] tw:line-through tw:decoration-[var(--error-400)]',
  add: 'tw:text-[color:var(--success-300)]',
  same: '',
};

const InlineDiffText: React.FC<{
  /** Current stored value of the field (the diff baseline). */
  before: string;
  /** Proposed value, or the reviewer's latest edit of it. */
  value: string;
  editKind: 'text' | 'textarea';
  onChange: (value: string) => void;
  /** Field label, for the accessible name of the view-mode control. */
  label: string;
  /** Non-owner viewer: show the diff but never open the editor. */
  readOnly?: boolean;
}> = ({ before, value, editKind, onChange, label, readOnly = false }) => {
  const [editing, setEditing] = useState(false);
  // Frozen copy of editKind captured when editing starts: callers derive
  // editKind from the live value's length (ObjectDiffRows' 80-char rule), so
  // using the prop directly would flip input↔textarea mid-typing — remounting
  // the control and throwing the caret to the end.
  const [activeEditKind, setActiveEditKind] = useState(editKind);
  const startEditing = () => {
    setActiveEditKind(editKind);
    setEditing(true);
  };
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea(textAreaRef, editing ? value : '', 3);

  const segments = useMemo(() => wordDiff(before, value), [before, value]);

  if (!editing || readOnly) {
    const interactiveProps = readOnly
      ? { 'aria-label': label }
      : {
          role: 'button',
          tabIndex: 0,
          'aria-label': `Edit ${label}`,
          title: 'Click to edit',
          onClick: startEditing,
          onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEditing();
            }
          },
        };
    return (
      <div
        {...interactiveProps}
        // Deliberately NOT .form-control (the app styles it display:flex,
        // which shreds flowing text); this mirrors its look on a block box.
        className={`tw:min-h-[38px] tw:w-full tw:whitespace-pre-wrap tw:break-words tw:border-b tw:border-[color:var(--border-neutral)] tw:px-3 tw:py-2 tw:text-[0.875rem] tw:font-semibold tw:leading-5 tw:text-[color:var(--text-white)] ${
          readOnly
            ? 'tw:cursor-default'
            : 'tw:cursor-text tw:hover:border-[color:var(--border-primary-active)]'
        }`}
      >
        {segments.length === 0 ? (
          <span className="tw:text-[color:var(--text-weak)]">—</span>
        ) : (
          segments.map((seg, i) => (
            <span key={i} className={SEGMENT_CLASSES[seg.type]}>
              {seg.text}
            </span>
          ))
        )}
      </div>
    );
  }

  if (activeEditKind === 'textarea') {
    return (
      <textarea
        ref={textAreaRef}
        className="form-control tw:max-h-[360px] tw:w-full tw:resize-y tw:overflow-y-auto"
        rows={3}
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
      />
    );
  }
  return (
    <input
      type="text"
      className="form-control"
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={() => setEditing(false)}
    />
  );
};

export default InlineDiffText;
